import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-10-05 18:30:16 +0200 (Mon, 05 Oct 2026) $
//  Author    : Dirk Rothe, Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 166276 $
//  Purpose   : wiki templates
// =============================================================================
// ---Third-party imports
import React, { Component } from 'react';
import { Label, Icon, Image } from 'semantic-ui-react';
import { Title, Style } from 'react-head';
// ---Framework imports
import log from 'uix/logging';
import { has, get, wordwrap, mapItems, dict, filterItems, keys, strip, values, flatten, isNil } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import { ucFirst, decodeTypes, decodeType, decodeAttribH5 } from 'uix/utils';
import { StandalonePart } from 'uix/parts';
import { Link, LoremBuilder, Tiles, Tile } from 'uix/components';
import { TeX } from 'uix/chunks';
import { useSelector, useGraphics, useState } from 'uix/hooks';
import { GenericTagHandler, TagMatcher } from './utils';
import { buildLanguageTemplate } from './languageSelection';
import { formatDate } from 'uix/utils/date';
// ---Globals
export * from './sui';
/*

== XML/etree API ==
tag: tagname
text: first child text node
tail: first next sibling text node
attrib: reactified attributes
  font-size => fontSize
  class => className

== tree axis support ==
children: list of children elements
parent: parent element

== other ==
i: i of node in relation to parent
data: more data

applyTemplates: call to process children - returns
  return (
    <div>{applyTemplates()}</div>>
  )
or
  return (
    <div>{applyTemplates()}</div>>
  )

== match examples ==

XSL: match="a[parent::div]"
JS: ({parent, tag}) => parent.tag == 'div' && tag == 'a'

XSL: match="a[@href]"
JS: ({tag, attrib}) => tag == 'a' && attrib.href

XSL: match="*[contains(@href, 'XXX')]"
JS: ({tag, attrib}) => tag == 'a' && attrib.href && attrib.href.includes('XXX')
or
JS: ({tag, attrib}) => tag == 'a' && get(attrib, 'href', '').includes('XXX')

XSL: match="div[count(span) = 1]"
JS: ({tag, children}) => tag == 'div' && children.filter(c => c.tag == 'span').length == 1

XSL: match="div[p]"
JS: ({tag, children}) => tag == 'div' && children.some(c => c.tag == 'p')

XSL: match="pre[preceding-sibling::p[1]]"
JS: ({tag, i, parent}) => tag == 'pre' && i > 0 && parent.children[i - 1].tag == 'p'

XSL: match="pre[preceding-sibling::p]"
JS: ({tag, i, parent}) => tag == 'pre' && i > 0 && parent.children.slice(0, i).some(c => c.tag == 'p')

*/ /* utility functions
 */ let nodePath = ({ i, parent })=>{
    /* return path to node given by index and parent */ let path = [
        i
    ];
    let p = parent;
    while(p){
        path.push(p.i);
        p = p.parent;
    }
    return path.reverse();
};
let traversePreceeding = ({ i, parent }, callback, _descended_)=>{
    /* execute callback for every preceeding element */ let children = parent.children;
    while(i > 0){
        i = i - 1;
        let element = children[i];
        let childCount = element.children.length;
        if (childCount > 0) {
            traversePreceeding({
                i: childCount,
                parent: element
            }, callback, true);
        }
        callback(element);
    }
    let nextParent = parent.parent;
    if (!_descended_ && nextParent) {
        traversePreceeding({
            i: parent.i,
            parent: nextParent
        }, callback);
    }
};
/* Matchers */ export const TilesMatcher = TagMatcher('Tiles', ({ childEls, applyTemplates, ...props })=>{
    childEls = childEls.filter((child)=>child.tag === 'Tile');
    let tilesChildren = childEls.map((child, i)=>/*#__PURE__*/ _jsx(Tile, {
            ...child.attrib
        }, i));
    return /*#__PURE__*/ _jsx(Tiles, {
        ...props,
        children: tilesChildren
    });
});
export const LoremIpsum = TagMatcher('LoremIpsum', ()=>/*#__PURE__*/ _jsx(LoremBuilder, {
        small: true
    }));
export const readmore = TagMatcher('readmore', ({ applyTemplates, height = '100px' })=>{
    /* build a container with "show more" effect
   */ let [open, setOpen] = useState(false);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                style: open ? {} : {
                    overflow: 'hidden',
                    height
                },
                children: applyTemplates()
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    cursor: 'pointer'
                },
                onClick: ()=>setOpen(!open),
                children: open ? 'Zeige weniger ...' : 'Zeige mehr ...'
            })
        ]
    });
});
const initialIsCapital = (word)=>word[0] !== word[0].toLowerCase();
// rewrite attributes of these tags (containing relative urls)
const TAG_API_REWRITE = {
    a: [
        'href'
    ],
    img: [
        'src'
    ],
    video: [
        'poster'
    ],
    source: [
        'src'
    ]
};
const rewrite_apiurl = (attrib, tag, apiurl, dname)=>{
    /* dev-server rewrite of relative urls
   inplace modification off "attrib"
   */ if (TAG_API_REWRITE[tag]) {
        let attrs = TAG_API_REWRITE[tag];
        attrs.filter((a)=>!isNil(attrib[a])).forEach((a)=>{
            let url = attrib[a];
            if (url.startsWith('/domainresource/') || url.startsWith('/domainimage/')) {
                url = `/${dname}${url}`;
            }
            if (url.startsWith('/')) {
                url = `${apiurl}${url}`;
            }
            attrib[a] = url;
        });
    }
};
export const node = {
    // low priority, matches last
    priority: -100,
    match: ()=>true,
    build: ({ tag, attrib, text, applyTemplates, data })=>{
        // stuff with uppercase initial letters are probably unmatched tags
        if (initialIsCapital(tag)) return /*#__PURE__*/ _jsx("tt", {
            children: `<${tag}>`
        });
        attrib = {
            ...attrib
        };
        if (data.apiurl) {
            // dev-mode
            // inplace edit
            rewrite_apiurl(attrib, tag, data.apiurl, data.dname);
        }
        // some elements are not allowed to have children (not even an empty list)
        // react error: XXX is a void element tag and must neither have `children` nor use `dangerouslySetInnerHTML`.
        let children = applyTemplates();
        // so we use null on length = 0
        children = children.length > 0 ? children : null;
        return /*#__PURE__*/ React.createElement(tag, attrib, children);
    }
};
export const variable = {
    match: ({ tag })=>tag == 'variable',
    build: ({ tag })=>{
        return /*#__PURE__*/ _jsx("tt", {
            children: "[var]"
        });
    }
};
let getDomainLabel = (language, dconfig, ddata)=>{
    let { webname, description, lang2description } = ddata;
    if (dconfig.templating.wiki.useDomainWebnameAsDomainDescription) {
        if (language !== 'de') {
            webname = lang2description[language] || webname;
        }
        return webname || description;
    }
    return description;
};
export const domainDescription = {
    match: ({ tag })=>tag == 'domainDescription',
    build: ({ language, dconfig, ddata })=>getDomainLabel(language, dconfig, ddata)
};
export const goLink = {
    match: ({ tag })=>tag == 'Go',
    build: ({ attrib, applyTemplates })=>{
        let { path, ...props } = attrib;
        props = decodeAttribH5(props);
        if (path) {
            // XXX: this doesnt look nice
            let parts = strip(path, '/').split('/');
            if (parts.length > 1) {
                props.classname = parts[0];
                props.tabname = parts[1];
                if (parts.length > 2) {
                    props.id = parts[2];
                } else {
                    props.id = null;
                }
            } else {
                return /*#__PURE__*/ _jsxs("span", {
                    children: [
                        "invalid path: ",
                        path
                    ]
                });
            }
        }
        return /*#__PURE__*/ _jsx(Link, {
            ...props,
            children: applyTemplates()
        });
    },
    completion: ({ classCfg })=>{
        let paths = flatten(values(classCfg).map((cfg)=>keys(cfg.tabs).map((t)=>`${cfg.name}/${t}`)));
        return {
            '!top': [
                'Go'
            ],
            Go: {
                attrs: {
                    id: [
                        '123'
                    ],
                    path: paths,
                    icon: [
                        'list',
                        'play'
                    ],
                    basic: [
                        'true',
                        'false'
                    ]
                }
            }
        };
    }
};
export const linkDetailedsearch = {
    match: ({ tag })=>tag == 'linkDetailedsearch',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[linkDetailedsearch]"
        })
};
export const linkHome = {
    match: ({ tag })=>tag == 'linkHome',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[linkHome]"
        })
};
export const linkImprint = {
    match: ({ tag })=>tag == 'linkImprint',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[linkImprint]"
        })
};
export const linkCollection = {
    match: ({ tag })=>tag == 'linkCollection',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[linkCollection]"
        })
};
export const linkPrivacy = {
    match: ({ tag })=>tag == 'linkPrivacy',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[linkPrivacy]"
        })
};
export const linkContact = {
    match: ({ tag })=>tag == 'linkContact',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[linkContact]"
        })
};
export const mainsearch = {
    match: ({ tag })=>tag == 'mainsearch',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[mainsearch]"
        })
};
export const imageButton = {
    match: ({ tag })=>tag == 'imageButton',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[imageButton]"
        })
};
export const chooseLanguage = {
    match: ({ tag })=>tag == 'chooseLanguage',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[chooseLanguage]"
        })
};
export const language = {
    match: ({ tag })=>tag == 'language' || tag == 'Language',
    build: buildLanguageTemplate
};
export const quicksearch = {
    match: ({ tag })=>tag == 'quicksearch',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[quicksearch]"
        })
};
export const socialnetworking = {
    match: ({ tag })=>tag == 'socialnetworking',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[socialnetworking]"
        })
};
export const libLogo = {
    match: ({ tag })=>tag == 'libLogo',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[libLogo]"
        })
};
export const lastNavUrl = {
    match: ({ tag })=>tag == 'lastNavUrl',
    build: ({ tag })=>/*#__PURE__*/ _jsx("tt", {
            children: "[lastNavUrl]"
        })
};
export const i18nLabel = {
    match: ({ tag, attrib })=>tag == 'span' && attrib.translate,
    build: ({ tag, attrib, util })=>/*#__PURE__*/ _jsx("span", {
            children: util.translate(attrib.translate)
        })
};
export const HeadTitle = {
    match: ({ tag, children })=>tag == 'HeadTitle',
    build: ({ text })=>/*#__PURE__*/ _jsx(Title, {
            children: text
        })
};
// react does not like blocklevel children for <p>
// and we emit alot of them - so convert to div
export const paragraph = {
    priority: -10,
    match: ({ tag, children })=>tag == 'p',
    build: ({ attrib, applyTemplates })=>/*#__PURE__*/ _jsx("div", {
            className: "para",
            ...attrib,
            children: applyTemplates()
        })
};
export const style = {
    /* attention: html5 python parser kills <style/>  if it's the first element*/ match: ({ tag, text })=>tag == 'style' && text,
    build: ({ text })=>/*#__PURE__*/ _jsx(Style, {
            children: text
        })
};
// react warns about tables without tbody, so add one
export const missingTableBody = {
    match: ({ tag, children })=>tag == 'table' && children.some((e)=>e.tag == 'tr'),
    build: ({ tag, attrib, applyTemplates })=>{
        // should there be a </thead>, we loose
        return /*#__PURE__*/ _jsx("table", {
            ...attrib,
            children: /*#__PURE__*/ _jsx("tbody", {
                children: applyTemplates()
            })
        });
    }
};
export const imageExt = {
    /* emitted for this markdown form
    {image|portrait|right|width=360|label=<a href="/445340">Selbstportrait</a>}
  */ match: ({ tag, attrib })=>tag == 'img',
    build: ({ tag, attrib, data })=>{
        if (attrib.vlext == 'true') {
            let ident = attrib.arg1;
            let style = attrib.arg2;
            let width = attrib.width;
            let src = `${data.apiurl}/${data.dname}/domainimage/${ident}`;
            let i_style = {
                width: `${width}px`,
                padding: '3px'
            };
            if (style == 'right') {
                i_style = {
                    ...i_style,
                    float: 'right',
                    marginLeft: '2em'
                };
            }
            return /*#__PURE__*/ _jsx("img", {
                src: src,
                style: i_style
            });
        } else {
            // modify src attribute
            let { src } = attrib;
            if (src?.startsWith('/')) {
                src = `${data.apiurl}${src}`;
            }
            return /*#__PURE__*/ _jsx("img", {
                ...attrib,
                src: src
            });
        }
    }
};
export const vlext = {
    /* Form: {ReleaseHistory|limit=5}
  XXX: this will now be transformed to PartElement form via markdown-py/vlext/reactify=true
  */ match: ({ tag, attrib })=>tag == 'span' && attrib.className == 'vlext',
    build: ({ tag, attrib, i, parent, data })=>{
        let name = attrib['data-vl'];
        let paramNames = keys(attrib).filter((e)=>e.startsWith('data-vl-') && e !== 'data-vl');
        //~ paramNames = get(attrib['data-vl-order-kwds'], , )
        let params = dict(paramNames.map((n)=>[
                n.slice(8),
                attrib[n]
            ]));
        let extBuilder = extensions[name];
        if (extBuilder) {
            return extBuilder({
                params,
                index: i,
                parent,
                data
            });
        }
        return buildStandalonePart(name, params, i, parent, data);
    }
};
const ImageLink = ({ url, title, imgdata })=>/*#__PURE__*/ _jsx("a", {
        href: url,
        target: "vl_wiki_out",
        title: title,
        style: {
            marginLeft: '3px'
        },
        children: /*#__PURE__*/ _jsx(Image, {
            inline: true,
            centered: true,
            height: "13em",
            width: "13em",
            src: imgdata
        })
    });
const WikipediaLink = ({ name })=>{
    let i18n = useSelector(Sel.i18n);
    let graphics = useGraphics([
        'connectors'
    ]);
    return /*#__PURE__*/ _jsx(ImageLink, {
        url: `https://${i18n._language}.wikipedia.org/wiki/${name}`,
        title: `${i18n.searchWikipediaFor}${name}`,
        imgdata: get(graphics.connectors, 'wiki_k')
    });
};
const GndLink = ({ gndid })=>{
    let i18n = useSelector(Sel.i18n);
    let graphics = useGraphics([
        'connectors'
    ]);
    return /*#__PURE__*/ _jsx(ImageLink, {
        url: `http://d-nb.info/gnd/${gndid}`,
        title: i18n.dnbLookup,
        imgdata: get(graphics.connectors, 'dnb')
    });
};
// non-part-based extensions - the simple ones
const extensions = {
    place: ({ params })=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                params.label || params.name,
                params.gnd && /*#__PURE__*/ _jsx(GndLink, {
                    gndid: params.gnd
                }),
                /*#__PURE__*/ _jsx(WikipediaLink, {
                    name: params.name
                })
            ]
        }),
    person: ({ params })=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                params.label || params.name,
                params.gnd && /*#__PURE__*/ _jsx(GndLink, {
                    gndid: params.gnd
                }),
                /*#__PURE__*/ _jsx(WikipediaLink, {
                    name: params.name
                })
            ]
        }),
    title: ({ params })=>/*#__PURE__*/ _jsx("a", {
            href: `#${params.urn || params.id}`,
            children: params.label
        }),
    page: ({ params })=>/*#__PURE__*/ _jsx(Link, {
            classname: "tree",
            tabname: "id",
            id: params.id,
            children: params.label
        })
};
//~ export const PartElement = {
//~ /* Form: <ReleaseHistory limit="5"> */
//~ match: ({tag, data}) => data.partNames.has(tag) || extensions[tag.toLowerCase()],
//~ build: ({tag, attrib, i, parent, data, applyTemplates, text}) => {
//~ let extBuilder = extensions[tag.toLowerCase()]
//~ if (extBuilder) {
//~ return extBuilder({params: attrib, index: i, parent, data})
//~ }
//~ let renderedChildren = (
//~ <>
//~ {text}
//~ {applyTemplates()}
//~ </>
//~ )
//~ return buildStandalonePart(tag, attrib, i, parent, data, renderedChildren)
//~ },
//~ completion: () => ({
//~ '!top': ['ReleaseHistory'],
//~ ReleaseHistory: {
//~ attrs: {
//~ max: ['5', '10', '20'],
//~ limit: [30],
//~ },
//~ children: [],
//~ },
//~ }),
//~ }
//~ export const part = {
//~ /* (deprectated) Form: <part name="ReleaseHistory" limit="5">
//~ */
//~ match: ({tag}) => tag == 'part',
//~ build: ({tag, attrib, i, parent, data}) => {
//~ let {name, ...params} = attrib
//~ if (!name) {
//~ return (
//~ <tt style={{border: '1px solid #CCC', padding: '3px'}}>
//~ Part without @name: {JSON.stringify(attrib)}
//~ </tt>
//~ )
//~ }
//~ return buildStandalonePart(name, params, i, parent, data)
//~ },
//~ }
let buildStandalonePart = (name, params, i, parent, data, renderedChildren)=>{
    // first build a unique key for this part to determine filterData store location
    // key should be stable
    let position = 0;
    let self = `part_${name}` // search for other parts with this name
    ;
    traversePreceeding({
        i,
        parent
    }, ({ tag, attrib })=>position += `part_${tag}` === self || `${tag}_${attrib.name}` === self || tag == 'span' && attrib.className == 'vlext' && `part_${attrib['data-vl']}` === self);
    let key = `${data.paneKey || 'wiki'}_${data.identifier}_${position}`;
    // filter empties and stuff that looks like a xml closing tag
    params = dict(filterItems(params, (name, value)=>value.trim() && !value.endsWith('>')));
    // decode simple types in params (bools, ints, floats, null)
    params = decodeTypes(params);
    if (renderedChildren) {
        params = {
            ...params,
            renderedChildren
        };
    }
    return /*#__PURE__*/ _jsx(StandalonePart, {
        name: ucFirst(name),
        storeKey: key,
        params: params
    });
};
// react warning
// br is a void element tag and must neither have `children` nor use `dangerouslySetInnerHTML`.
// see https://developer.mozilla.org/en-US/docs/Glossary/Void_element
const VOID_ELS = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'source',
    'track',
    'wbr'
]);
export const ensureEmptyElements = {
    match: ({ tag })=>VOID_ELS.has(tag),
    build: ({ tag, attrib })=>{
        return /*#__PURE__*/ React.createElement(tag, {
            ...attrib
        });
    }
};
// XXX: unclear vl-ext situation
let matchExtension = (name, { tag, children })=>tag == 'p' && children.some((e)=>e.tag == 'span' && e.attrib.className == 'vlext' && e.attrib['data-vl'] == name);
let matchBraceToplevelExt = (name, { tag, children })=>tag == 'p' && children.some((e)=>e.tag == name);
// XXX: unclear vl-ext situation
export const emailTemplate = {
    match: ({ tag, children })=>matchExtension('email', {
            tag,
            children
        }),
    build: ({ children, util, data })=>{
        let ext = children.filter((e)=>e.tag == 'span' && e.attrib.className == 'vlext')[0];
        let msg_type = ext.attrib['data-vl-type'];
        let testMail = null;
        if (true || ext.attrib['data-vl-bcc'] && util.hasGroup('debug')) {
            // api call: sendTestMailTemplate?wiki_ident={ancestor::wiki/@identifier}&amp;msg_type={$mailType}
            let action = Actions.callApi('sendTestMailTemplate', {
                wiki_ident: data.identifier,
                msg_type
            });
            testMail = /*#__PURE__*/ _jsx("a", {
                href: "#",
                onClick: ()=>util.dispatch(action),
                children: "test"
            });
        }
        return /*#__PURE__*/ _jsxs("h4", {
            children: [
                `E-Mail: ${ext.attrib['data-vl-subject']} (`,
                /*#__PURE__*/ _jsx("code", {
                    style: {
                        whitespace: 'wrap'
                    },
                    children: msg_type
                }),
                ') ',
                testMail
            ]
        });
    }
};
export const math_lxml_parsed = {
    match: ({ tag, attrib })=>tag == 'script' && attrib.type === 'math/tex',
    build: ({ attrib, text, tail })=>[
            /*#__PURE__*/ _jsx(TeX, {
                math: text
            }),
            tail
        ]
};
export const math_html5_parsed = {
    match: ({ tag, attrib })=>tag === 'mathjax' && attrib.type === 'math/tex',
    build: ({ text, tail })=>[
            /*#__PURE__*/ _jsx(TeX, {
                math: text
            }),
            tail
        ]
};
const TRAC = 'https://vl-support.semantics.de';
export const ticket = {
    match: ({ tag })=>tag == 'Ticket',
    build: ({ attrib, text, tail, util })=>{
        let date = /*#__PURE__*/ _jsx("span", {
            style: {
                fontWeight: 'bold',
                color: '#666'
            },
            children: `${formatDate(attrib.date, 'mm / yyyy')} | `
        });
        let desc = /*#__PURE__*/ _jsxs("span", {
            children: [
                date,
                `Ticket ${attrib.arg1} ${attrib.arg1 ? ': ' + attrib.arg2 : ''}`
            ]
        });
        if (!util.hasGroup('guest')) {
            return /*#__PURE__*/ _jsx("a", {
                href: `${TRAC}/ticket/${attrib.arg1}`,
                children: desc
            });
        }
        return desc;
    }
};
// no rendering for these
let IGNORE = new Set([
    'Cors'
]);
export const ignore = {
    priority: 100,
    match: ({ tag, attrib, children })=>tag == 'p' && children.some((e)=>IGNORE.has(e.tag)),
    build: ()=>null
};
export const CRASH = TagMatcher('CRASH', ()=>XYZ);
export const ReleaseHistory = TagMatcher('ReleaseHistory', ()=>'ReleaseHistory');
export const Searchbox = TagMatcher('Searchbox', ()=>'Searchbox');
export const Hero = TagMatcher('Hero', ()=>'Hero');
export const BlogList = TagMatcher('BlogList', ()=>'BlogList');
export const Card = TagMatcher('Card', ()=>'Card');
export const Cards = TagMatcher('Cards', ()=>'Cards');
